'use client';

import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { createPortal } from 'react-dom';
import { PrV2ScrollableContent } from '../pr-v2-scrollable-content';
import { PrV2LoadingSkeleton } from './pr-v2-loading-skeleton';
import { usePrV2SkeletonLayout } from './use-pr-v2-skeleton-layout';
import { getStoredSidebarWidth } from '@/components/ui/sidebar-width';
import { PR_V2_CHAT_SIDEBAR_DEFAULT_WIDTH } from '@/lib/stores/pr-v2-view-preferences';
import { cn } from '@/lib/utils';
const ENTRANCE_CLASS_NAME = {
  'in-window': 'animate-in fade-in fill-mode-backwards delay-150 duration-150',
  'window-reveal': ''
} as const;
export type PrV2NavigationOverlayEntrance = keyof typeof ENTRANCE_CLASS_NAME;

/**
 * Optimistic stand-in for the PR page while a client navigation's RSC round
 * trip is in flight. The route cannot serve a prefetched static shell (its
 * layout is deliberately dynamic from the first byte so document loads keep
 * real 404 statuses), so PrNavigationProvider paints this overlay the moment
 * navigation starts. It stays up until the PR page commits. The route renders no
 * fallback of its own on client navigations, so nothing swaps in between
 * (see the PR route's layout.tsx).
 *
 * The PR page's macro layout depends on persisted viewer preferences, so the
 * overlay mirrors them to avoid layout shift on the swap: a header-height bar
 * where BaseTemplate's top nav sits, a rail where the chat sidebar will be,
 * the layout's own scroll container around the skeleton (its padding and
 * scrollbar gutter set where the content starts and how wide it is),
 * and zen mode hiding the AI sections column. Despite its name,
 * PrV2SidebarRight renders side="left" on
 * desktop with offsetDesktopHeader ("right" is its mobile side), so the rail
 * sits on the LEFT, below the header bar — not a full-height right column.
 *
 * The 150ms fade-in delay keeps near-instant navigations (router-cache
 * bounce-backs) from flashing it. If the RSC fetch fails outright Next falls
 * back to a hard browser navigation, which unloads the page — so the overlay
 * cannot strand the user.
 */
export function PrV2NavigationLoadingOverlay(t0) {
  const $ = _c(14);
  const {
    entrance
  } = t0;
  const layout = usePrV2SkeletonLayout();
  const [chatSidebarWidth] = React.useState(_temp);
  const t1 = ENTRANCE_CLASS_NAME[entrance];
  let t2;
  if ($[0] !== t1) {
    t2 = cn("fixed inset-0 z-50 flex flex-col overflow-hidden bg-background", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div aria-hidden={true} className="h-(--header-height) shrink-0 border-b border-border bg-background" />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== chatSidebarWidth || $[4] !== layout.chatSidebarOpen) {
    t4 = layout.chatSidebarOpen && <div aria-hidden={true} data-testid="pr-nav-chat-sidebar-rail" className="hidden shrink-0 border-r border-border bg-background md:block" style={{
      width: chatSidebarWidth
    }} />;
    $[3] = chatSidebarWidth;
    $[4] = layout.chatSidebarOpen;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== layout) {
    t5 = <PrV2ScrollableContent><PrV2LoadingSkeleton layout={layout} /></PrV2ScrollableContent>;
    $[6] = layout;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t4 || $[9] !== t5) {
    t6 = <div className="flex min-h-0 flex-1">{t4}{t5}</div>;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t2 || $[12] !== t6) {
    t7 = createPortal(<div role="status" aria-label="Opening pull request" className={t2}>{t3}{t6}</div>, document.body);
    $[11] = t2;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}
function _temp() {
  return getStoredSidebarWidth(PR_V2_CHAT_SIDEBAR_DEFAULT_WIDTH);
}